import { useCallback, useEffect, useState } from "react"

// ** Third Party Components
import { useQuery } from "@tanstack/react-query"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

// ** UI Components
import { Button } from "@components/common/button"
import { Input } from "@pma/components/common/input"
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"

// ** Layouts
import DashboardLayout from "@pma/app/dashboard/layout"

// ** Store
import { useAuth } from "@stores/auth-store"
import { useModal } from "@stores/modal-store"

// ** Type
import { DictType } from "@pma/types"

// ** Utils
import { cn, debounce } from "@pma/utils/helpers"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

// ** Services
import { getOrganizationUserListService } from "@pma/services/common"

// ** Local
import UsersDataTable from "./users-data-table"

const UsersPage = () => {
  const modal = useModal()
  const { authConfig, signIn } = useAuth()
  const { translate, locale } = useTranslator()

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()

  const isHebrew = locale === "he"
  const queryKey = searchParam.get("revalidate")
  const orgId = authConfig?.last_organization_id

  const [users, setUsers] = useState<DictType[]>([])
  const [searchValue, setSearchValue] = useState("")
  const [globalFilter, setGlobalFilter] = useState("")
  const [pagination, setPagination] = useState({ pageIndex: 1, pageSize: 25 })

  const handleSearch = useCallback((searchString: string) => {
    setSearchValue(searchString)
  }, [])

  const debouncingFunction = useCallback(debounce(handleSearch, 800), [handleSearch])

  useEffect(() => {
    debouncingFunction(globalFilter)
  }, [globalFilter])

  const {
    data: orgUsers,
    refetch,
    isFetched,
    isLoading,
  } = useQuery({
    queryKey: ["org-users", orgId, pagination.pageIndex, pagination.pageSize, searchValue],
    queryFn: () =>
      getOrganizationUserListService({
        organization_id: orgId,
        paging_per_page: pagination.pageSize,
        paging_start_page: pagination.pageIndex,
        search: searchValue,
      }),
    enabled: !!orgId,
  })

  useEffect(() => {
    setPagination({ pageIndex: 1, pageSize: 25 })
  }, [searchValue])

  useEffect(() => {
    if (!orgUsers) return
    const incoming: DictType[] = orgUsers.items ?? []
    setUsers((prev) => {
      const shouldAppend =
        incoming.length > 0 && !searchValue && prev.length > 0 && pagination.pageIndex > 1
      return shouldAppend ? [...prev, ...incoming] : [...incoming]
    })
  }, [orgUsers])

  useEffect(() => {
    signIn({ organizationUsers: orgUsers?.items })
  }, [isFetched])

  useEffect(() => {
    if (queryKey === "users") {
      refetch()
      navigate(`${location.pathname}`)
    }
  }, [queryKey])

  return (
    <DashboardLayout>
      <Card className='my-6 space-y-3 p-0'>
        <CardHeader>
          <CardTitle
            className={cn(
              "flex w-full flex-col items-center justify-between text-xl font-semibold md:flex-row",
              isHebrew ? "text-right" : "text-left",
            )}
          >
            <span>{translate("Users")}</span>
            <div className='flex flex-col items-center md:flex-row'>
              <Input
                placeholder={`${translate("Search")}...`}
                value={globalFilter}
                onChange={(event) => setGlobalFilter(event.target.value)}
                className={cn(
                  "my-3 h-8 w-full md:my-0 md:w-64",
                  isHebrew ? "ml-0 md:ml-3" : "mr-0 md:mr-3",
                )}
              />
              <Button
                size='sm'
                onClick={() => {
                  modal.onOpen()
                  modal.setContext("invite")
                }}
                className='w-full md:w-24'
              >
                {translate("Invite User")}{" "}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className='p-2 md:p-4'>
          <UsersDataTable
            data={users}
            isLoading={isLoading}
            hasNextPage={!!orgUsers?.nextPage}
            onLoadMore={() =>
              setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))
            }
            pageSize={pagination.pageSize}
            pageIndex={pagination.pageIndex}
            onPageSizeChange={(pageSize) => setPagination({ pageIndex: 1, pageSize })}
          />
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}

export default UsersPage
